/* src/components/product/ProductBrowser.tsx */

"use client";

import { useState, useEffect } from "react";
// 假设 ProductGrid 组件会进行修改以支持分页
import { ProductGrid } from "./ProductGrid";
import { Package } from "lucide-react";

type ProductPreview = { id: string; name: string; imageUrl: string };
type Catalogue = {
	id: string;
	name: string;
	products: ProductPreview[];
};

type ProductBrowserProps = {
	catalogues: Catalogue[];
	noProductsText: string;
};

// 1. 定义一个新的、更结构化的 state 类型，用于保存在 localStorage 中
type LastViewState = {
	activeId: string;
	// pages 对象用于存储每个 catalogue ID 对应的页码
	pages: { [catalogueId: string]: number };
};

// 2. 更新 localStorage 的 key
const LAST_VIEW_STATE_KEY = "@next/lastViewState";

export function ProductBrowser({
	catalogues,
	noProductsText,
}: ProductBrowserProps) {
	const [activeCatalogueId, setActiveCatalogueId] = useState("all");
	// 3. 新增一个 state 来管理所有分类的页码状态
	const [pageStates, setPageStates] = useState<{
		[catalogueId: string]: number;
	}>({});
	const [isClient, setIsClient] = useState(false);

	// 4. 修改 useEffect，用于在组件加载时从 localStorage 读取完整的状态
	useEffect(() => {
		setIsClient(true);
		try {
			const savedStateJSON = localStorage.getItem(LAST_VIEW_STATE_KEY);
			if (savedStateJSON) {
				const savedState: LastViewState = JSON.parse(savedStateJSON);

				// 校验保存在 localStorage 中的分类 ID 在当前是否依然有效
				if (
					savedState.activeId &&
					catalogues.some((c) => c.id === savedState.activeId)
				) {
					setActiveCatalogueId(savedState.activeId);
				}
				// 恢复所有分类的页码
				if (savedState.pages) {
					setPageStates(savedState.pages);
				}
			}
		} catch (error) {
			console.error("Failed to parse last view state from localStorage", error);
			localStorage.removeItem(LAST_VIEW_STATE_KEY); // 如果解析失败，则清除无效数据
		}
		// 此 effect 只应在客户端初次挂载时运行一次，用于状态恢复
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// 5. 修改 useEffect，当分类或页码状态变化时，保存完整状态到 localStorage
	useEffect(() => {
		if (isClient) {
			const stateToSave: LastViewState = {
				activeId: activeCatalogueId,
				pages: pageStates,
			};
			localStorage.setItem(LAST_VIEW_STATE_KEY, JSON.stringify(stateToSave));
		}
	}, [activeCatalogueId, pageStates, isClient]);

	// 6. 定义一个处理页码变化的回调函数
	const handlePageChange = (newPage: number) => {
		// 更新当前激活分类的页码
		setPageStates((prevStates) => ({
			...prevStates,
			[activeCatalogueId]: newPage,
		}));
	};

	const activeCatalogue = catalogues.find((c) => c.id === activeCatalogueId);
	const productsToShow = activeCatalogue ? activeCatalogue.products : [];
	// 7. 获取当前激活分类的页码，如果不存在则默认为 1
	const currentPage = pageStates[activeCatalogueId] || 1;

	return (
		<div>
			<div className="border-b border-gray-200 mb-12">
				<nav className="-mb-px flex gap-x-8" aria-label="Tabs">
					{catalogues.map((catalogue) => (
						<button
							key={catalogue.id}
							// 切换分类时，只会改变 activeCatalogueId，页码会从 pageStates 中自动获取
							onClick={() => setActiveCatalogueId(catalogue.id)}
							className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-semibold transition-colors duration-200 focus:outline-none ${
								activeCatalogueId === catalogue.id
									? "border-[#1a3d5d] text-[#1a3d5d]"
									: "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
							}`}
						>
							{catalogue.name}
						</button>
					))}
				</nav>
			</div>

			{productsToShow.length > 0 ? (
				// 8. 将当前页码和页码更新函数传递给 ProductGrid 组件
				<ProductGrid
					products={productsToShow}
					currentPage={currentPage}
					onPageChange={handlePageChange}
				/>
			) : (
				<div className="text-center text-gray-500 py-16">
					<Package className="w-16 h-16 mx-auto text-gray-400" />
					<p className="mt-4 text-lg">{noProductsText}</p>
				</div>
			)}
		</div>
	);
}
